import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { assetsApi, assetCategoriesApi, assetStatesApi, assetLocationsApi, AssignableUser } from '../../api';
import { AssetMinimal, AssetCategory, AssetState, AssetLocation, Asset, CreateAssetData, UpdateAssetData } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { usePermissions } from '../../hooks/usePermissions';
import './Assets.css';

const PER_PAGE = 50;

export function Assets() {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [assets, setAssets] = useState<AssetMinimal[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [states, setStates] = useState<AssetState[]>([]);
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [selectedState, setSelectedState] = useState<number | ''>('');
  const [selectedLocation, setSelectedLocation] = useState<number | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [showFormPassword, setShowFormPassword] = useState(false);

  const [formData, setFormData] = useState<CreateAssetData>({
    asset_id: '',
    category_id: 0,
    state_id: undefined,
    location_id: undefined,
    serial_number: '',
    invoice_number: '',
    purchase_bill: '',
    purchase_date: '',
    value: undefined,
    warranty_expiry: '',
    host_name: '',
    cpu: '',
    ram: '',
    hdd: '',
    ssd: '',
    graphics_card: '',
    model_no: '',
    os: '',
    charger: '',
    ip_address: '',
    wlan_mac: '',
    lan_mac: '',
    software_installed: '',
    password: '',
    courier_user: '',
    courier_email: '',
    remark: '',
  });

  const [assignData, setAssignData] = useState({
    assigned_to: 0,
    expected_return: '',
    notes: '',
  });

  const [returnData, setReturnData] = useState({
    return_condition: '',
    return_notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [assetsRes, categoriesRes, statesRes, locationsRes] = await Promise.all([
        assetsApi.getAssets({
          category_id: selectedCategory || undefined,
          state_id: selectedState || undefined,
          location_id: selectedLocation || undefined,
          search: searchTerm || undefined,
          include_inactive: showInactive,
        }),
        assetCategoriesApi.getCategories(),
        assetStatesApi.getStates(),
        assetLocationsApi.getLocations(),
      ]);
      
      setAssets(assetsRes.assets);
      setCategories(categoriesRes.categories);
      setStates(statesRes.states);
      setLocations(locationsRes.locations);

      // Fetch assignable users if user has assign permission (excludes Super Admin)
      if (hasPermission('assets.assign')) {
        try {
          const usersRes = await assetsApi.getAssignableUsers();
          setUsers(usersRes.users);
        } catch {
          // Ignore if can't fetch users
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedState, selectedLocation, searchTerm, showInactive, hasPermission]);

  // Pagination calculations
  const totalPages = Math.ceil(assets.length / PER_PAGE);
  const paginatedAssets = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return assets.slice(start, start + PER_PAGE);
  }, [assets, currentPage]);
  const startItem = assets.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endItem = Math.min(currentPage * PER_PAGE, assets.length);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle URL parameters for status filter and create action
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const actionParam = searchParams.get('action');
    
    // Handle create action from dashboard
    if (actionParam === 'create' && hasPermission('assets.create')) {
      setShowModal(true);
      // Clear the action param from URL
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Handle status filter - map status name to state_id
    if (statusParam && states.length > 0) {
      const statusMapping: Record<string, string[]> = {
        'assigned': ['assigned'],
        'available': ['available'],
        'maintenance': ['under maintenance', 'maintenance'],
        'broken': ['broken', 'retired', 'broken/retired'],
      };
      
      const searchTerms = statusMapping[statusParam.toLowerCase()] || [];
      const matchedState = states.find(s => 
        searchTerms.some(term => s.name.toLowerCase().includes(term))
      );
      
      if (matchedState) {
        setSelectedState(matchedState.id);
        // Clear the status param from URL after applying
        searchParams.delete('status');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, states, hasPermission]);

  const handleSearch = () => {
    fetchData();
  };

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await assetsApi.createAsset(formData);
      await fetchData();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create asset');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAsset) return;
    try {
      setLoading(true);
      const updateData: UpdateAssetData = { ...formData };
      await assetsApi.updateAsset(editingAsset.id, updateData);
      await fetchData();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update asset');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      setLoading(true);
      await assetsApi.deleteAsset(assetId);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete asset');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAsset) return;
    try {
      setLoading(true);
      await assetsApi.assignAsset(selectedAsset.id, {
        assigned_to: assignData.assigned_to,
        expected_return: assignData.expected_return || undefined,
        notes: assignData.notes || undefined,
      });
      await fetchData();
      setShowAssignModal(false);
      setSelectedAsset(null);
      setAssignData({ assigned_to: 0, expected_return: '', notes: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign asset');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAsset) return;
    try {
      setLoading(true);
      await assetsApi.returnAsset(selectedAsset.id, {
        return_condition: returnData.return_condition || undefined,
        return_notes: returnData.return_notes || undefined,
      });
      await fetchData();
      setShowReturnModal(false);
      setSelectedAsset(null);
      setReturnData({ return_condition: '', return_notes: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return asset');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (assetId: number) => {
    try {
      setLoading(true);
      const res = await assetsApi.getAsset(assetId);
      const asset = res.asset;
      setEditingAsset(asset);
      setFormData({
        asset_id: asset.asset_id,
        category_id: asset.category_id,
        state_id: asset.state_id,
        location_id: asset.location_id || undefined,
        serial_number: asset.serial_number || '',
        invoice_number: asset.invoice_number || '',
        purchase_bill: asset.purchase_bill || '',
        purchase_date: asset.purchase_date || '',
        value: asset.value || undefined,
        warranty_expiry: asset.warranty_expiry || '',
        host_name: asset.host_name || '',
        cpu: asset.cpu || '',
        ram: asset.ram || '',
        hdd: asset.hdd || '',
        ssd: asset.ssd || '',
        graphics_card: asset.graphics_card || '',
        model_no: asset.model_no || '',
        os: asset.os || '',
        charger: asset.charger || '',
        ip_address: asset.ip_address || '',
        wlan_mac: asset.wlan_mac || '',
        lan_mac: asset.lan_mac || '',
        software_installed: asset.software_installed || '',
        password: asset.password || '',
        courier_user: asset.courier_user || '',
        courier_email: asset.courier_email || '',
        remark: asset.remark || '',
      });
      setShowModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = async (assetId: number) => {
    try {
      setLoading(true);
      const res = await assetsApi.getAsset(assetId);
      setSelectedAsset(res.asset);
      setShowDetailModal(true);
      setShowPassword(false);
      setRevealedPassword(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const revealPassword = async () => {
    if (!selectedAsset) return;
    try {
      const res = await assetsApi.getAssetPassword(selectedAsset.id);
      setRevealedPassword(res.password);
      setShowPassword(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reveal password');
    }
  };

  const openAssignModal = async (assetId: number) => {
    try {
      setLoading(true);
      const res = await assetsApi.getAsset(assetId);
      setSelectedAsset(res.asset);
      setShowAssignModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const openReturnModal = async (assetId: number) => {
    try {
      setLoading(true);
      const res = await assetsApi.getAsset(assetId);
      setSelectedAsset(res.asset);
      setShowReturnModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingAsset(null);
    setShowFormPassword(false);
    setFormData({
      asset_id: '',
      category_id: 0,
      state_id: undefined,
      location_id: undefined,
      serial_number: '',
      invoice_number: '',
      purchase_bill: '',
      purchase_date: '',
      value: undefined,
      warranty_expiry: '',
      host_name: '',
      cpu: '',
      ram: '',
      hdd: '',
      ssd: '',
      graphics_card: '',
      model_no: '',
      os: '',
      charger: '',
      ip_address: '',
      wlan_mac: '',
      lan_mac: '',
      software_installed: '',
      password: '',
      courier_user: '',
      courier_email: '',
      remark: '',
    });
  };

  return (
    <div className="assets-page">
      <div className="assets-header">
        <div className="assets-title">
          <h1>Asset Inventory</h1>
          <p>Manage all IT assets and equipment</p>
        </div>
        {hasPermission('assets.create') && (
          <Button onClick={() => setShowModal(true)}>
            <Icons.Plus /> Add Asset
          </Button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card className="filters-card">
        <div className="asset-filters-panel">
          <div className="filter-search-row">
            <Input
              type="text"
              placeholder="Search by Asset ID, Serial, Model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              leftIcon={<Icons.Search />}
            />
            <Button className="search-btn" onClick={handleSearch}>Search</Button>
          </div>
          <div className="filter-dropdowns-row">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : '')}
              className="filter-select"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value ? Number(e.target.value) : '')}
              className="filter-select"
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state.id} value={state.id}>{state.name}</option>
              ))}
            </select>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value ? Number(e.target.value) : '')}
              className="filter-select"
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show Inactive
            </label>
          </div>
        </div>
      </Card>

      <Card>

        {loading ? (
          <div className="loading-state">Loading assets...</div>
        ) : paginatedAssets.length === 0 ? (
          <div className="empty-state">
            <Icons.Laptop />
            <h3>No assets found</h3>
            <p>Add your first asset to get started</p>
          </div>
        ) : (
          <div className="assets-table-wrapper">
            <table className="assets-table">
              <thead>
                <tr>
                  <th>Asset ID</th>
                  <th>Category</th>
                  <th>Model</th>
                  <th>Serial Number</th>
                  <th>State</th>
                  <th>Location</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAssets.map(asset => (
                  <tr key={asset.id} className={!asset.is_active ? 'inactive-row' : ''}>
                    <td>
                      <button 
                        className="link-button"
                        onClick={() => openDetailModal(asset.id)}
                      >
                        {asset.asset_id}
                      </button>
                    </td>
                    <td>{asset.category_name}</td>
                    <td>{asset.model_no || '-'}</td>
                    <td>{asset.serial_number || '-'}</td>
                    <td>
                      <span 
                        className="state-badge"
                        style={{ backgroundColor: asset.state_color || '#6b7280' }}
                      >
                        {asset.state_name}
                      </span>
                    </td>
                    <td>{asset.location_name || '-'}</td>
                    <td>{asset.assignee_name || <span className="text-muted">Unassigned</span>}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="icon-button"
                          title="View Details"
                          onClick={() => openDetailModal(asset.id)}
                        >
                          <Icons.Eye />
                        </button>
                        {hasPermission('assets.edit') && (
                          <button 
                            className="icon-button"
                            title="Edit"
                            onClick={() => openEditModal(asset.id)}
                          >
                            <Icons.Edit />
                          </button>
                        )}
                        {hasPermission('assets.assign') && !asset.assigned_to && (
                          <button 
                            className="icon-button assign-btn"
                            title="Assign"
                            onClick={() => openAssignModal(asset.id)}
                          >
                            <Icons.UserPlus />
                          </button>
                        )}
                        {hasPermission('assets.return') && asset.assigned_to && (
                          <button 
                            className="icon-button return-btn"
                            title="Return"
                            onClick={() => openReturnModal(asset.id)}
                          >
                            <Icons.UserMinus />
                          </button>
                        )}
                        {hasPermission('assets.delete') && (
                          <button 
                            className="icon-button delete-btn"
                            title="Delete"
                            onClick={() => handleDeleteAsset(asset.id)}
                          >
                            <Icons.Trash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {assets.length > 0 ? startItem : 0} to {endItem} of {assets.length} assets
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <Icons.ChevronsLeft />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <Icons.ChevronLeft />
            </button>

            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page-num ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <Icons.ChevronsRight />
            </button>
          </div>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</h2>
              <button className="close-button" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={editingAsset ? handleUpdateAsset : handleCreateAsset}>
              <div className="modal-body">
                <div className="form-section">
                  <h3>Basic Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Asset ID *</label>
                      <Input
                        value={formData.asset_id}
                        onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                        placeholder="e.g., OMDT047"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
                        required
                      >
                        <option value={0}>Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <select
                        value={formData.state_id || ''}
                        onChange={(e) => setFormData({ ...formData, state_id: e.target.value ? Number(e.target.value) : undefined })}
                      >
                        <option value="">Default State</option>
                        {states.map(state => (
                          <option key={state.id} value={state.id}>{state.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Location</label>
                      <select
                        value={formData.location_id || ''}
                        onChange={(e) => setFormData({ ...formData, location_id: e.target.value ? Number(e.target.value) : undefined })}
                      >
                        <option value="">Select Location</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Purchase Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Serial Number</label>
                      <Input
                        value={formData.serial_number || ''}
                        onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Invoice Number</label>
                      <Input
                        value={formData.invoice_number || ''}
                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Purchase Bill</label>
                      <Input
                        value={formData.purchase_bill || ''}
                        onChange={(e) => setFormData({ ...formData, purchase_bill: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Purchase Date</label>
                      <Input
                        type="date"
                        value={formData.purchase_date || ''}
                        onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Value</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.value || ''}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Warranty Expiry</label>
                      <Input
                        type="date"
                        value={formData.warranty_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Hardware Specifications</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Model No</label>
                      <Input
                        value={formData.model_no || ''}
                        onChange={(e) => setFormData({ ...formData, model_no: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Host Name</label>
                      <Input
                        value={formData.host_name || ''}
                        onChange={(e) => setFormData({ ...formData, host_name: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>CPU</label>
                      <Input
                        value={formData.cpu || ''}
                        onChange={(e) => setFormData({ ...formData, cpu: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>RAM</label>
                      <Input
                        value={formData.ram || ''}
                        onChange={(e) => setFormData({ ...formData, ram: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>HDD</label>
                      <Input
                        value={formData.hdd || ''}
                        onChange={(e) => setFormData({ ...formData, hdd: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>SSD</label>
                      <Input
                        value={formData.ssd || ''}
                        onChange={(e) => setFormData({ ...formData, ssd: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Graphics Card</label>
                      <Input
                        value={formData.graphics_card || ''}
                        onChange={(e) => setFormData({ ...formData, graphics_card: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>OS</label>
                      <Input
                        value={formData.os || ''}
                        onChange={(e) => setFormData({ ...formData, os: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Charger</label>
                      <Input
                        value={formData.charger || ''}
                        onChange={(e) => setFormData({ ...formData, charger: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Network Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>IP Address</label>
                      <Input
                        value={formData.ip_address || ''}
                        onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>WLAN MAC</label>
                      <Input
                        value={formData.wlan_mac || ''}
                        onChange={(e) => setFormData({ ...formData, wlan_mac: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>LAN MAC</label>
                      <Input
                        value={formData.lan_mac || ''}
                        onChange={(e) => setFormData({ ...formData, lan_mac: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Software & Security</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Software Installed</label>
                      <textarea
                        value={formData.software_installed || ''}
                        onChange={(e) => setFormData({ ...formData, software_installed: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                      <div className="password-input-wrapper">
                        <Input
                          type={showFormPassword ? 'text' : 'password'}
                          value={formData.password || ''}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowFormPassword(!showFormPassword)}
                        >
                          {showFormPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Other Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Courier/Recipient</label>
                      <Input
                        value={formData.courier_user || ''}
                        onChange={(e) => setFormData({ ...formData, courier_user: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Courier Email</label>
                      <Input
                        type="email"
                        value={formData.courier_email || ''}
                        onChange={(e) => setFormData({ ...formData, courier_email: e.target.value })}
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Remarks</label>
                      <textarea
                        value={formData.remark || ''}
                        onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={loading}>
                  {editingAsset ? 'Update Asset' : 'Create Asset'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAsset && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Asset Details - {selectedAsset.asset_id}</h2>
              <button className="close-button" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-section">
                  <h3>Basic Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Asset ID:</span>
                    <span className="detail-value">{selectedAsset.asset_id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Category:</span>
                    <span className="detail-value">{selectedAsset.category_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">State:</span>
                    <span className="detail-value">
                      <span 
                        className="state-badge"
                        style={{ backgroundColor: selectedAsset.state_color || '#6b7280' }}
                      >
                        {selectedAsset.state_name}
                      </span>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{selectedAsset.location_name || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Assigned To:</span>
                    <span className="detail-value">{selectedAsset.assignee_name || 'Unassigned'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Purchase Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Serial Number:</span>
                    <span className="detail-value">{selectedAsset.serial_number || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Invoice:</span>
                    <span className="detail-value">{selectedAsset.invoice_number || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Purchase Bill:</span>
                    <span className="detail-value">{selectedAsset.purchase_bill || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Purchase Date:</span>
                    <span className="detail-value">{selectedAsset.purchase_date || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Value:</span>
                    <span className="detail-value">{selectedAsset.value ? `₹${selectedAsset.value.toLocaleString()}` : '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Warranty Expiry:</span>
                    <span className="detail-value">{selectedAsset.warranty_expiry || '-'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Hardware Specifications</h3>
                  <div className="detail-row">
                    <span className="detail-label">Model:</span>
                    <span className="detail-value">{selectedAsset.model_no || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Host Name:</span>
                    <span className="detail-value">{selectedAsset.host_name || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">CPU:</span>
                    <span className="detail-value">{selectedAsset.cpu || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">RAM:</span>
                    <span className="detail-value">{selectedAsset.ram || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">HDD:</span>
                    <span className="detail-value">{selectedAsset.hdd || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">SSD:</span>
                    <span className="detail-value">{selectedAsset.ssd || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Graphics Card:</span>
                    <span className="detail-value">{selectedAsset.graphics_card || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">OS:</span>
                    <span className="detail-value">{selectedAsset.os || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Charger:</span>
                    <span className="detail-value">{selectedAsset.charger || '-'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Network Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">IP Address:</span>
                    <span className="detail-value">{selectedAsset.ip_address || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">WLAN MAC:</span>
                    <span className="detail-value">{selectedAsset.wlan_mac || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">LAN MAC:</span>
                    <span className="detail-value">{selectedAsset.lan_mac || '-'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Software & Security</h3>
                  <div className="detail-row">
                    <span className="detail-label">Software:</span>
                    <span className="detail-value">{selectedAsset.software_installed || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Password:</span>
                    <span className="detail-value password-field">
                      {showPassword ? (revealedPassword || '-') : '••••••••'}
                      {hasPermission('assets.edit') && (
                        <button 
                          className="reveal-btn"
                          onClick={revealPassword}
                          type="button"
                        >
                          {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                        </button>
                      )}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Other Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Courier/Recipient:</span>
                    <span className="detail-value">{selectedAsset.courier_user || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Courier Email:</span>
                    <span className="detail-value">{selectedAsset.courier_email || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Remarks:</span>
                    <span className="detail-value">{selectedAsset.remark || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedAsset && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Asset - {selectedAsset.asset_id}</h2>
              <button className="close-button" onClick={() => setShowAssignModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleAssignAsset}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Assign To *</label>
                  <select
                    value={assignData.assigned_to}
                    onChange={(e) => setAssignData({ ...assignData, assigned_to: Number(e.target.value) })}
                    required
                  >
                    <option value={0}>Select Employee</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}){user.department ? ` - ${user.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Expected Return Date</label>
                  <Input
                    type="date"
                    value={assignData.expected_return}
                    onChange={(e) => setAssignData({ ...assignData, expected_return: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={assignData.notes}
                    onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                    rows={3}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                <Button type="submit" disabled={loading || !assignData.assigned_to}>Assign</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && selectedAsset && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Return Asset - {selectedAsset.asset_id}</h2>
              <button className="close-button" onClick={() => setShowReturnModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleReturnAsset}>
              <div className="modal-body">
                <p className="return-info">
                  Returning asset from: <strong>{selectedAsset.assignee_name}</strong>
                </p>
                <div className="form-group">
                  <label>Return Condition</label>
                  <select
                    value={returnData.return_condition}
                    onChange={(e) => setReturnData({ ...returnData, return_condition: e.target.value })}
                  >
                    <option value="">Select Condition</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                    <option value="Damaged">Damaged</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={returnData.return_notes}
                    onChange={(e) => setReturnData({ ...returnData, return_notes: e.target.value })}
                    rows={3}
                    placeholder="Any notes about the return..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowReturnModal(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>Process Return</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assets;
